import { ProjectActions } from '@octopus-do/octopus-project-core'
import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { commitProjectRequest } from '@storeOld/actionCreators'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import API from '@/api'
import { isFigma, isFigmaUrl } from '@/helpers'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { Input } from './components/Input'
import css from './NodeGalleryFigmaLink.module.scss'

type Props = {
  nodeId: string
  closeButton: React.ReactNode
  closeModal: () => void
}

const NodeGalleryFigmaLink: React.FC<Props> = ({
  nodeId,
  closeButton,
  closeModal,
}) => {
  const dispatch = useDispatch()

  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const artboardDisabled = useArtBoardDisabled()

  const validate = () => {
    if (!url) {
      setError('Please enter a URL')

      return false
    }

    if (!isFigmaUrl(url)) {
      setError('Please enter a valid URL')

      return false
    }

    return true
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setError('')
    setLoading(false)

    const validated = validate()

    if (!validated) {
      return
    }

    setLoading(true)

    const { groups }: any = isFigma.exec(url)

    const key = groups.key2 ? groups.key2 : groups.key
    const node = groups.node2 ? groups.node2 : groups.node

    try {
      const res = await API.Projects.getFigmaDocument(projectId, {
        fileId: key,
        nodeId: node.replace('-', '%3A'),
      })

      dispatch(
        commitProjectRequest(
          ProjectActions.nodes.setFigmaPreview({
            id: nodeId,
            figma: {
              figmaUrl: url,
              page: res.page,
              project: res.project,
              preview: res.previewUrl,
              thumbnail: res.thumbnailUrl,
            },
          })
        )
      )

      closeModal()
    } catch (e) {
      const err = e as Error
      const message =
        err.message || '{"type":"error","message":"Something went wrong"}'

      const { message: errorMessage } = JSON.parse(message)

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setError('')
  }, [url])

  return (
    <div className={css.container}>
      <div className={css.close}>{closeButton}</div>

      <div className={css.title}>Figma</div>

      <form onSubmit={onSubmit}>
        <div className={css.body}>
          <Input onChange={setUrl} error={error} loading={loading} />

          <Button
            className={css.button}
            type="submit"
            label="Import"
            loading={loading}
            disabled={artboardDisabled}
          />
        </div>
      </form>
    </div>
  )
}

export default NodeGalleryFigmaLink
